import { useContext, useMemo } from 'react';
import { createCachedSelector } from 're-reselect';
import { useQuery } from 'react-query';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { generatePositionWageValues } from '@ubeya/shared-web/utils/positions';
import * as api from '../../services/api';
import { SORT_BY_STRING } from '../../utils/sorting';
import useCRUD from '../useCRUD';
import { mappedArray } from '../../utils/array';
import {
  COMPASS_DE_ORGANIZATION_ID,
  CONSTELATION_ACCOUNT_ID,
  CONSTELLATION_IRELAND_ACCOUNT_ID,
  CONSTELLATION_PLATINUM_ACCOUNT_ID,
  hasRestrictedFeatures,
  LEGENDS_ACCOUNT_IDS,
  OVG_ACCOUNT_IDS
} from '../../utils/accounts';
import useAccount from './useAccount';
import useBranches from './useBranches';

const HIDE_POSITION_CREATOR_ACCOUNT_IDS = [CONSTELLATION_PLATINUM_ACCOUNT_ID];
const HIDE_POSITION_CREATOR_ORGANIZATION_IDS = [COMPASS_DE_ORGANIZATION_ID];

const selector = createCachedSelector(
  (data) => data.data,
  (data, t) => t,
  (data, t, currency) => currency,
  (data, t, currency, accountId) => accountId,
  (data, t, currency, accountId, branchesOptions) => branchesOptions,
  (data, t, currency, accountId, branchesOptions, organizationId) => organizationId,
  (data, t, currency, accountId, branchesOptions, organizationId) => {
    const labelSorter = SORT_BY_STRING('label');
    const nameSorter = SORT_BY_STRING('slug');

    const formattedPositions = (data || []).map((position) => {
      const selectedWage = generatePositionWageValues({
        wage: position.wage,
        wages: position.wages || [],
        externalWage: position.clientWage,
        externalWageKey: 'clientWage'
      });

      const payRate = selectedWage?.wage?.hourlyWage;

      const slugWithPayRate =
        hasRestrictedFeatures({ accountId, organizationId }) ||
        OVG_ACCOUNT_IDS.includes(accountId) ||
        LEGENDS_ACCOUNT_IDS.includes(accountId)
          ? `${t(position.slug)}${payRate ? ` (${currency}${payRate})` : ``}`
          : t(position.slug);

      const hidePositionCreator =
        HIDE_POSITION_CREATOR_ACCOUNT_IDS.includes(accountId) ||
        HIDE_POSITION_CREATOR_ORGANIZATION_IDS.includes(organizationId);

      const slug =
        accountId === CONSTELATION_ACCOUNT_ID || accountId === CONSTELLATION_IRELAND_ACCOUNT_ID
          ? slugWithPayRate
          : `${slugWithPayRate}${
              position.createdByClientId && !hidePositionCreator ? ` (${position.clientAccountName})` : ``
            }`;

      return {
        ...position,
        slug,
        prevSlug: position.slug
      };
    });

    const positions = formattedPositions.filter(({ isDeleted }) => !isDeleted).sort(nameSorter);

    const visibleBranchIds =
      Array.isArray(branchesOptions) && branchesOptions.length > 0
        ? new Set(branchesOptions.map(({ value }) => value))
        : null;

    const hasBranchesFilter = visibleBranchIds && visibleBranchIds.size > 0;

    const filteredPositions = hasBranchesFilter
      ? positions.filter(
          ({ branchIds: pBranchIds }) => !pBranchIds?.length || pBranchIds.some((id) => visibleBranchIds.has(id))
        )
      : positions;

    const positionsOptions = filteredPositions.map(({ id, slug }) => ({ value: id, label: slug })).sort(labelSorter);

    const filteredPositionsIds = new Set(filteredPositions.map(({ id }) => id));

    const filteredPositionsByAllowedBranchIds = positions.filter(({ id }) => filteredPositionsIds.has(id));

    const mappedPositions = mappedArray(
      formattedPositions,
      ({ id }) => id,
      (position) => ({ ...position, label: position.slug })
    );

    return {
      positions,
      positionsOptions,
      filteredPositionsByAllowedBranchIds,
      positionsIncludeDeleted: formattedPositions,
      mappedPositions
    };
  }
)({
  keySelector: (data, t, currency, accountId, branchesOptions, organizationId, storeKey) => storeKey
});

const usePositions = () => {
  const { t, currency, organizationId } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { branchesOptions } = useBranches();

  const storeKey = ['positions', accountId];
  const { isLoading, data, refetch } = useQuery(storeKey, () => api.fetchPositions({ accountId }), {
    enabled: !!accountId,
    select: (positionsData) =>
      selector(positionsData, t, currency, accountId, branchesOptions, organizationId, storeKey.join('#'))
  });

  const {
    mappedPositions = {},
    positions = [],
    positionsOptions = [],
    positionsIncludeDeleted = [],
    filteredPositionsByAllowedBranchIds = []
  } = useMemo(() => {
    const {
      mappedPositions: rawMappedPositions = {},
      positions: rawPositions = [],
      positionsOptions: rawPositionsOptions = [],
      positionsIncludeDeleted: rawPositionsIncludeDeleted = [],
      filteredPositionsByAllowedBranchIds: rawFilteredPositionsByAllowedBranchIds = []
    } = data || {};
    return {
      mappedPositions: rawMappedPositions,
      positions: rawPositions,
      positionsOptions: rawPositionsOptions,
      positionsIncludeDeleted: rawPositionsIncludeDeleted,
      filteredPositionsByAllowedBranchIds: rawFilteredPositionsByAllowedBranchIds
    };
  }, [data]);

  const { addItem, editItem, deleteItem } = useCRUD(
    storeKey,
    {
      addApi: (position) => api.addPosition({ accountId, position }),
      editApi: ({ id, ...position }) => api.updatePosition({ accountId, positionId: id, position }),
      deleteApi: ({ id }) => api.deletePosition({ accountId, positionId: id })
    },
    { refetchOnSuccessDelete: true }
  );

  return {
    isLoading,
    refetch,
    mappedPositions,
    positionsIncludeDeleted,
    positions,
    filteredPositionsByAllowedBranchIds,
    positionsOptions,
    addPosition: addItem,
    deletePosition: deleteItem,
    editPosition: editItem
  };
};

export default usePositions;
